import { themeQuartz } from "ag-grid-enterprise";
import GridDataFormater from "utils/GridDataFormater";

/**
 * Base configuration that all grids inherit
 * This is the minimum configuration every grid should have
 */
const baseGridOptions = {
  // Theme
  theme: themeQuartz,

  // Layout
  domLayout: "normal",
  // Removed autoSizeStrategy to let flex columns handle sizing naturally

  // Pagination
  pagination: true,
  paginationPageSize: 10,
  paginationPageSizeSelector: [10, 25, 50, 100],
  paginationAutoPageSize: false,

  // Row Configuration
  rowHeight: 40,
  headerHeight: 54,
  enableCellTextSelection: true,

  // Column Configuration
  defaultColDef: {
    sortable: true,
    filter: true,
    resizable: true,
    enableCellChangeFlash: true,
    enableRowGroup: true,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    // Row Selection
    rowSelection: "multiple",
    suppressRowClickSelection: true,

    // IMPORTANT: Enable column moving by NOT suppressing it
    suppressMovableColumns: false,
    suppressColumnMoveAnimation: false,

    // Sorting
    multiSortKey: "ctrl",

    // Performance
    animateRows: true,

    // Export Configuration
    defaultExcelExportParams: {
      author: "Ascent360",
    },
    defaultCsvExportParams: {
      suppressQuotes: false,
    },
  }
};

/**
 * Feature flags for different grid capabilities
 * Components can enable features they need via configuration
 */
export const GridFeatures = {
  SIDE_BAR: "sideBar",
  ROW_GROUPING: "rowGrouping",
  CONTEXT_MENU: "contextMenu",
  FLOATING_FILTERS: "floatingFilters",
  MANAGE_COLUMNS: "manageColumns",
  ROW_PINNING: "rowPinning",
  RANGE_SELECTION: "rangeSelection",
};

/**
 * Feature configurations that can be mixed and matched
 */
const featureConfigs = {
  [GridFeatures.SIDE_BAR]: {
    sideBar: {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: (params) => {
              // Hide columns without field (like actions column)
              // Or columns in disabled fields list
              return !params.column.getColDef().field;
            },
            suppressColumnSelectAll: false,
            contractColumnSelection: false,
            // suppressColumnMove: (params) => {
            //   // Prevent moving columns without field
            //   return !params.column.getColDef().field;
            // },
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
      ],
      defaultToolPanel: "",
      hiddenByDefault: false,
    },
  },

  [GridFeatures.ROW_GROUPING]: {
    rowGroupPanelShow: "always",
    groupDisplayType: "singleColumn",
    suppressRowGroupHidesColumns: false,
  },

  [GridFeatures.CONTEXT_MENU]: {
    enableRangeSelection: true,
    getContextMenuItems: (params) => [
      "autoSizeAll",
      "separator",
      "copy",
      "copyWithHeaders",
      "separator",
      "resetColumns",
      "separator",
      "export",
      "separator",
      {
        name: "Pin Row",
        subMenu: [
          {
            name: "Pin to Top",
            action: (params) => {
              const rowNode = params.node;
              if (rowNode) {
                const existingTopRows = params.api.getGridOption("pinnedTopRowData") || [];
                const isAlreadyPinned = existingTopRows.some(row => 
                  JSON.stringify(row) === JSON.stringify(rowNode.data)
                );
                if (!isAlreadyPinned) {
                  params.api.setGridOption("pinnedTopRowData", [...existingTopRows, rowNode.data]);
                }
              }
            },
          },
          {
            name: "Pin to Bottom", 
            action: (params) => {
              const rowNode = params.node;
              if (rowNode) {
                const existingBottomRows = params.api.getGridOption("pinnedBottomRowData") || [];
                const isAlreadyPinned = existingBottomRows.some(row => 
                  JSON.stringify(row) === JSON.stringify(rowNode.data)
                );
                if (!isAlreadyPinned) {
                  params.api.setGridOption("pinnedBottomRowData", [...existingBottomRows, rowNode.data]);
                }
              }
            },
          },
          {
            name: "Unpin Row",
            action: (params) => {
              const rowNode = params.node;
              if (rowNode) {
                const existingTopRows = params.api.getGridOption("pinnedTopRowData") || [];
                const existingBottomRows = params.api.getGridOption("pinnedBottomRowData") || [];
                
                const filteredTopRows = existingTopRows.filter(row => 
                  JSON.stringify(row) !== JSON.stringify(rowNode.data)
                );
                const filteredBottomRows = existingBottomRows.filter(row => 
                  JSON.stringify(row) !== JSON.stringify(rowNode.data)
                );
                
                params.api.setGridOption("pinnedTopRowData", filteredTopRows);
                params.api.setGridOption("pinnedBottomRowData", filteredBottomRows);
              }
            },
          },
          {
            name: "Unpin All Rows",
            action: (params) => {
              params.api.setGridOption("pinnedTopRowData", []);
              params.api.setGridOption("pinnedBottomRowData", []);
            },
          },
        ],
      },
    ],
  },

  [GridFeatures.FLOATING_FILTERS]: {
    floatingFilter: true,
  },

  [GridFeatures.ROW_PINNING]: {
    suppressRowPinning: false,
  },

  [GridFeatures.RANGE_SELECTION]: {
    enableRangeSelection: true,
    enableCharts: false,
  },
};

/**
 * Main function to create grid options with specific features
 */
export const createGridOptions = (features = [], customOptions = {}) => {
  let options = { ...baseGridOptions };

  features.forEach((feature) => {
    if (featureConfigs[feature]) {
      options = { ...options, ...featureConfigs[feature] };
    }
  });

  options = { ...options, ...customOptions };

  return options;
};

/**
 * Pre-configured grid options for common use cases
 */
export const gridPresets = {
  //basic: createGridOptions(),
  basic: {
    ...baseGridOptions,
    defaultColDef: {
      ...baseGridOptions.defaultColDef,
      suppressMenu: true,
      resizable: true,
    },
    suppressContextMenu: true,
    getContextMenuItems: null,
  },

  standard: createGridOptions([
    GridFeatures.SIDE_BAR,
    GridFeatures.CONTEXT_MENU,
    GridFeatures.ROW_GROUPING,
  ]),

  advanced: createGridOptions([
    GridFeatures.SIDE_BAR,
    GridFeatures.CONTEXT_MENU,
    GridFeatures.ROW_GROUPING,
    GridFeatures.FLOATING_FILTERS,
    GridFeatures.ROW_PINNING,
    GridFeatures.RANGE_SELECTION,
  ]),
};

export const defaultGridOptions = gridPresets.basic;

/**
 * Helper function to get column alignment based on field type
 */
export const getColumnAlignment = (fieldType) => {
  const alignmentMap = {
    text: "left",
    number: "right",
    date: "right",
    boolean: "left",
  };

  return {
    headerClass: `ag-header-${alignmentMap[fieldType] || "left"}`,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      justifyContent:
        fieldType === "number" || fieldType === "date"
          ? "flex-end"
          : "flex-start",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  };
};

/**
 * Helper to create column definitions with proper typing and alignment
 * Automatically applies date formatter for date fields
 * Auto-sizes columns when width is not explicitly provided
 */
export const createColumnDef = (config) => {
  const {
    field,
    headerName,
    fieldType = "text",
    width,
    flex,
    minWidth,
    maxWidth,
    hide = false,
    customOrder = 0,
    valueFormatter,
    ...rest
  } = config;

  const columnDef = {
    field,
    headerName,
    hide,
    customOrder,
    suppressMovable: false, // ALLOW DRAG AND DROP
    sortable: true,
    resizable: true,
    filter: true,
    ...getColumnAlignment(fieldType),
    ...rest,
  };

  // Handle width/flex behavior: prioritize explicit width, then explicit flex, then default flex
  if (width !== undefined) {
    columnDef.width = width;
  } else if (flex !== undefined) {
    columnDef.flex = flex;
    // Set default min/max for flex columns if not explicitly provided
    if (minWidth === undefined) {
      columnDef.minWidth = 140; // Sufficient width to prevent most header wrapping
    }
    if (maxWidth === undefined) {
      columnDef.maxWidth = 400; // Allow columns to be wider when needed
    }
  } else {
    // Use flex for proportional sizing but with better min/max constraints
    columnDef.flex = 1;
    columnDef.minWidth = 140; // Sufficient width to prevent most header wrapping
    columnDef.maxWidth = 400; // Allow columns to be wider when needed
  }

  // Always allow minWidth and maxWidth to be set regardless of flex/width
  if (minWidth !== undefined) {
    columnDef.minWidth = minWidth;
  }
  if (maxWidth !== undefined) {
    columnDef.maxWidth = maxWidth;
  }

  // Auto-apply date formatter if fieldType is date and no custom formatter provided
  if (fieldType === "date" && !valueFormatter) {
    columnDef.valueFormatter = GridDataFormater.vNextformatDateTimefromUTC;
  } else if (valueFormatter) {
    columnDef.valueFormatter = valueFormatter;
  }

  return columnDef;
};

/**
 * Export file name generator
 */
export const generateExportFileName = (reportType, clientId, designId) => {
  const date = new Date();
  const yymmdd = `${date.getFullYear().toString().slice(-2)}${String(
    date.getMonth() + 1
  ).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;

  return `${reportType}_${clientId}_${designId}_${yymmdd}`;
};